import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@llt-frontend/react-material-button';
import { AnalyticsEventTrigger } from '@llt-frontend/react-analytics-context';
import { formatPrice } from './util';
import packageImg from './package.png';

interface RepurchaseItemProps {
  upsell?: boolean;
  packageData: any;
}

export const RepurchaseItem: React.FC<RepurchaseItemProps> = ({ upsell, packageData }) => {
  const { t, ready: translationLoaded } = useTranslation('self-service/packages/repurchase');

  // duration
  const durationHours = packageData.duration_seconds / 60 / 60;

  // offer pricing
  const offer = packageData.offer_listings?.[0];
  const offerPriceCents = offer?.price.amount ?? 0;
  const offerSavedCents = offer?.amount_saved.amount ?? 0;

  // price formatting
  const originalPrice = formatPrice(packageData.price_cents);
  const finalPriceCents = offerPriceCents > 0 ? offerPriceCents : packageData.price_cents;
  const priceOff = formatPrice(offerSavedCents);
  const priceTotal = formatPrice(finalPriceCents);
  const pricePerHour = formatPrice(finalPriceCents / durationHours);

  if (!translationLoaded) {
    return null;
  }

  return (
    <div className="repurchase-item">
      <div className="repurchase-tag">
        <div>
          <p>{t('duration', { durationHours })}</p>
        </div>
        {upsell ? (
          <>
            <p className="short">{t('saveShort')}</p>
            <p className="full">{t('saveFull')}</p>
          </>
        ) : (
          <p>{t('lastSize')}</p>
        )}
      </div>
      <div className="repurchase-info">
        <div className="repurchase-img" style={{ backgroundImage: `url('${packageImg}')` }} />
        <div className="repurchase-details">
          <p>
            <strong>{t('itemTitle', { durationHours })}</strong>
          </p>
          <p>{packageData.package_type_name}</p>
          <p>
            <strong>
              {t('itemSessions', {
                sessionCount: packageData.duration_seconds / packageData.session_length_seconds,
                sessionMinutes: packageData.session_length_seconds / 60,
              })}
            </strong>
          </p>
        </div>
      </div>
      <div className="repurchase-cta">
        <div>
          <h6>{priceTotal}</h6> <p>{t('ctaPricePerHour', { pricePerHour })}</p>
          {offerSavedCents > 0 && (
            <div className="repurchase-offer">
              <p className="repurchase-original-price">{originalPrice}</p>
              <p className="repurchase-price-off">{t('priceOff', { priceOff })}</p>
            </div>
          )}
        </div>
        <AnalyticsEventTrigger
          analytics={{
            action: 'select-package-recommended',
            category: 'tutoring-packages',
            label: `id-${packageData.id}`,
          }}
          element={Button}
          elementProps={{
            contained: true,
            className: 'repurchase-item-cta',
            href: `/payments/buynow_terminal/index.html?packageId=${packageData.id}${
              offer?.promotion_id ? `&promotionId=${offer.promotion_id}` : ''
            }`,
          }}
        >
          {t('ctaButton')}
        </AnalyticsEventTrigger>
      </div>
    </div>
  );
};
